import React, { useState } from "react";
import { useMediaQuery, useTheme, IconButton } from "@mui/material";
import { useSelector } from "react-redux";
import MenuIcon from "@mui/icons-material/Menu";
import styled from "@emotion/styled";
import {
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  Divider
} from "@mui/material";
import {
  Work as WorkIcon,
  ListAlt as ProcessIcon,
  Description as DescriptionIcon,
  BarChart as ReportIcon,
  ExitToApp as ExitToAppIcon,
  People as PeopleIcon
} from "@mui/icons-material";
import ViewListIcon from "@mui/icons-material/ViewList";
import {
  DrawerHeader,
  DrawerStyled,
  ListItemStyled,
  ListItemTextStyled,
} from "./DashboardComponents";
import { Logo } from "./LogoStyles";
import { paths } from "../routes/paths";
import { useLocation, useParams } from "react-router-dom";
import BusinessIcon from "@mui/icons-material/Business";
import AssuredWorkloadIcon from "@mui/icons-material/AssuredWorkload";
import ViewQuiltIcon from "@mui/icons-material/ViewQuilt";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { setUiPreference, UI_V1 } from "../utils/uiPreference";

const ResponsiveDrawer = ({
  navigate,
  logoImage,
  handleListItemClick,
  drawerOpen,
  handleDrawerToggle,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { jobId } = useParams();
  const AdminCompany = localStorage.getItem("is_admin") === "true";
  const Job = useSelector((state) => state.appreducer.selectedJob);
  const hasJobSelected = !!Job;
  const userId = useSelector((state) => state.appreducer.account.userId);

  const isAdmin = localStorage.getItem("role") === "super_admin";

  const handleMobileDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleItemClick = (event, path) => {
    handleListItemClick(event, path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const allNavigationItems = [
    {
      text: "Home",
      icon: <ViewQuiltIcon />,
      path: paths.dashboard.jobs,
      alwaysShow: true,
    },
    {
      text: "Resume Pool",
      icon: <PeopleIcon />,
      path: paths.dashboard.resumePool,
      alwaysShow: true,
    },
  ];

  const navigationItems = allNavigationItems.filter(
    (item) => item.alwaysShow || hasJobSelected
  );

  const drawerContent = (
    <>
      <DrawerHeader onClick={() => navigate(paths.dashboard.jobs)}>
        <Logo src={logoImage} alt="HR-AI Logo" />
      </DrawerHeader>
      
      <Divider sx={{ 
        borderColor: '#a2adbb',
      }} />

      <List sx={{ flexGrow: 1, paddingTop: "16px", paddingBottom: "16px" }}>
        {navigationItems.map((item) => (
          <ListItemStyled
            button
            key={item.text}
            selected={location.pathname === item.path}
            onClick={(event) => handleItemClick(event, item.path)}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemTextStyled
              primary={item.text}
              primaryTypographyProps={{
                style: {
                  fontFamily: "Raleway",
                  fontSize: "14px",
                  fontWeight: 600,
                  lineHeight: "16.44px",
                  letterSpacing: "0.2px",
                  textAlign: "left",
                },
              }}
            />
          </ListItemStyled>
        ))}

        {isAdmin && (
          <ListItemStyled
            button
            selected={location.pathname === paths.dashboard.companies}
            onClick={(event) => handleItemClick(event, paths.dashboard.companies)}
          >
            <ListItemIcon>
              <AdminPanelSettingsIcon />
            </ListItemIcon>
            <ListItemTextStyled
              primary="Admin Company"
              primaryTypographyProps={{
                style: {
                  fontFamily: "Raleway",
                  fontSize: "14px",
                  fontWeight: 600,
                  lineHeight: "16.44px",
                  letterSpacing: "0.2px",
                  textAlign: "left",
                },
              }}
            />
          </ListItemStyled>
        )}

        {AdminCompany && (
          <ListItemStyled
            button
            selected={location.pathname === paths.dashboard.companyUsers}
            onClick={(event) => handleItemClick(event, paths.dashboard.companyUsers)}
          >
            <ListItemIcon>
              <AssuredWorkloadIcon />
            </ListItemIcon>
            <ListItemTextStyled
              primary="Company Configuration"
              primaryTypographyProps={{
                style: {
                  fontFamily: "Raleway",
                  fontSize: "14px",
                  fontWeight: 600,
                  lineHeight: "16.44px",
                  letterSpacing: "0.2px",
                  textAlign: "left",
                },
              }}
            />
          </ListItemStyled>
        )}
      </List>

      {/* Mirrors "Switch to old version" in the v1 rail, so the two UIs are reachable from
          each other rather than only via the login prompt — which someone may have declined
          months ago and can no longer find. Sets the preference, so future logins land in
          v1 too. Full page load, not navigate(): the two UIs have separate theme roots. */}
      <List sx={{ paddingTop: 0, paddingBottom: 0 }}>
        <ListItemStyled
          button
          key="switch-to-v1"
          onClick={() => {
            setUiPreference(UI_V1);
            window.location.href = paths.v1.jobs;
          }}
        >
          <ListItemIcon>
            <AutoAwesomeIcon sx={{ color: "#4C4CDD" }} />
          </ListItemIcon>
          <ListItemTextStyled
            primary="Try the new console"
            secondary="Switch to the new version"
            primaryTypographyProps={{
              style: {
                fontFamily: "Raleway",
                fontSize: "14px",
                fontWeight: 600,
                lineHeight: "16.44px",
                letterSpacing: "0.2px",
                textAlign: "left",
                color: "#4C4CDD",
              },
            }}
            secondaryTypographyProps={{
              style: {
                fontFamily: "Raleway",
                fontSize: "11px",
                fontWeight: 500,
                letterSpacing: "0.2px",
                textAlign: "left",
                color: "#8E8E99",
              },
            }}
          />
        </ListItemStyled>
      </List>

      <List sx={{ paddingTop: "16px", paddingBottom: "16px" }}>
        <ListItemStyled
          button
          key="logout"
          onClick={(event) => handleItemClick(event, "logout")}
        >
          <ListItemIcon>
            <ExitToAppIcon />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{
              style: {
                fontFamily: "Raleway",
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "16.44px",
                letterSpacing: "0.3px",
                textAlign: "left",
              },
            }}
          />
        </ListItemStyled>
      </List>
    </>
  );

  return (
    <>
      {isMobile && (
        <MobileMenuButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleMobileDrawerToggle}
        >
          <MenuIcon />
        </MobileMenuButton>
      )}

      {isMobile ? (
        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={handleMobileDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            "& .MuiDrawer-paper": {
              width: "245px",
              height: "100vh",
              boxSizing: "border-box",
              backgroundColor: "#f2f4f870",
              border: "none",
              borderRadius: 0,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <DrawerStyled
          variant="persistent"
          open={drawerOpen}
          sx={{
            width: drawerOpen ? "245px" : 0,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: "245px",
              height: "100vh",
              boxSizing: "border-box",
              backgroundColor: "#f2f4f870",
              transition: "transform 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
              transform: drawerOpen ? "translateX(0)" : "translateX(-100%)",
              border: "none",
              borderRadius: 0,
            },
          }}
        >
          {drawerContent}
        </DrawerStyled>
      )}
    </>
  );
};

const MobileMenuButton = styled(IconButton)({
  position: "fixed",
  top: "10px",
  left: "10px",
  zIndex: 1200,
});

export default ResponsiveDrawer;
